// OTR-04 — broker-agnostic frontend connection probe.
//
// Polls /api/brokers/<id>/health every POLL_INTERVAL_MS and mirrors the
// shape of isHealthy from api/brokers/schwab/auth.js. Consumers gate the
// OMS / surface reauth banners off the returned flags. The hook itself
// is broker-agnostic — HEALTH_ENDPOINTS and RECONNECT_URLS are the only
// schwab-specific surfaces; PT-02 extends them for IBKR.
//
// Network errors during probe deliberately preserve last-known-good
// state. Health is a UI signal, not a hard contract — intermittent
// failures shouldn't gate the Send Orders button.

import { useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

const POLL_INTERVAL_MS = 60_000

const HEALTH_ENDPOINTS = {
  schwab: '/api/brokers/schwab/health',
}

const RECONNECT_URLS = {
  schwab: '/api/schwab/auth?action=login',
}

const INITIAL_STATE = {
  ok: true,
  reason: null,
  requiresReauth: false,
  expiresAt: null,
}

export function useBrokerConnection(brokerId) {
  const { user } = useAuth()
  const [state, setState] = useState(INITIAL_STATE)
  const stateRef = useRef(state)
  stateRef.current = state

  const endpoint = HEALTH_ENDPOINTS[brokerId]

  const probe = useCallback(async () => {
    if (!endpoint) return
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) return
      const res = await fetch(endpoint, {
        method: 'GET',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (!res.ok) return
      const body = await res.json()
      setState({
        ok: !!body.ok,
        reason: body.reason ?? null,
        requiresReauth: body.reason === 'reauth_required',
        expiresAt: body.expiresAt ?? null,
      })
    } catch {
      // Preserve last-known-good — see header comment.
    }
  }, [endpoint])

  useEffect(() => {
    if (!endpoint || !user) return
    probe()
    const id = setInterval(probe, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [endpoint, user?.id, probe])

  const reconnect = useCallback(() => {
    const url = RECONNECT_URLS[brokerId]
    if (!url) return
    window.location.href = url
  }, [brokerId])

  const refresh = useCallback(async () => {
    await probe()
  }, [probe])

  return {
    ok: state.ok,
    reason: state.reason,
    requiresReauth: state.requiresReauth,
    expiresAt: state.expiresAt,
    reconnect,
    refresh,
  }
}
